import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, Send, X, AlertTriangle, ShieldAlert } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const StoreMessageBox = ({ storeId, storeName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (isOpen && token) fetchMessages();
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/store/${storeId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data.messages || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    if (!token) { toast.error('Mesaj gondermek icin giris yapin'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/store/${storeId}/messages`,
        { message: newMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Mesaj gonderilemedi';
      toast.error(detail);
    } finally { setSending(false); }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => {
          if (!token) { toast.error('Mesaj gondermek icin giris yapin'); return; }
          setIsOpen(true);
        }}
        className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 transition-colors"
        data-testid="ask-seller-btn"
      >
        <MessageSquare className="h-4 w-4" /> Saticiya Soru Sor
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 w-96 bg-white rounded-2xl shadow-2xl border z-50 flex flex-col max-h-[500px]" data-testid="message-box">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-orange-500 text-white rounded-t-2xl">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          <span className="font-medium text-sm">{storeName}</span>
        </div>
        <button onClick={() => setIsOpen(false)} className="hover:bg-orange-600 rounded-lg p-1">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-start gap-2">
        <ShieldAlert className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-amber-700">
          Pazarlo kurallari geregi mesajlarda telefon numarasi, adres, email veya sosyal medya bilgisi paylasilamaz. Ihlal durumunda hesabiniz askiya alinabilir.
        </p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px] max-h-[300px]">
        {loading && <div className="text-center text-sm text-slate-400">Yukleniyor...</div>}
        {!loading && messages.length === 0 && (
          <div className="text-center text-sm text-slate-400 py-8">Henuz mesaj yok. Saticiya sorunuzu yazmaya baslayin.</div>
        )}
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
              msg.sender_type === 'customer'
                ? 'bg-orange-500 text-white rounded-br-none'
                : 'bg-slate-100 text-slate-800 rounded-bl-none'
            }`}>
              <p>{msg.message}</p>
              <p className={`text-xs mt-1 ${msg.sender_type === 'customer' ? 'text-orange-200' : 'text-slate-400'}`}>
                {new Date(msg.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t p-3 flex gap-2">
        <input
          value={newMessage}
          onChange={e => setNewMessage(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          placeholder="Mesajinizi yazin..."
          className="flex-1 border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
          disabled={sending}
          data-testid="message-input"
        />
        <button
          onClick={sendMessage}
          disabled={sending || !newMessage.trim()}
          className="bg-orange-500 text-white rounded-xl px-3 py-2 hover:bg-orange-600 disabled:opacity-50 transition-colors"
          data-testid="send-message-btn"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default StoreMessageBox;
